import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CarVerificationList.module.scss';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, MAX_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { useToasts } from 'react-toast-notifications';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import InputSearch from 'common/components/inputSearch/InputSearch';
import { useDebouncedCallback } from 'use-debounce';
import { CarVerificationListItemDto } from 'api/carVerification/models/CarVerificationDto';
import { CarVerificationSearchCriteria } from 'api/carVerification/models/CarVerificationSearchCriteria';
import CarVerificationService from 'api/carVerification/CarVerificationService';
import Popover from 'common/components/popover/Popover';
import VerificationFiltersScreen, { Filters } from 'screens/verification/components/list-filters/VerificationFiltersScreen';
import { useHistory, useParams } from 'react-router-dom';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import RequestVerificationScreen from 'screens/verification/components/request-verification/RequestVerificationScreen';
import Button from 'common/components/button/Button';
import ReactTooltip from 'react-tooltip';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';
import { VehicleDto } from 'api/vehicles/models/VehicleDto';
import VerificationListTable from 'screens/verification/components/verificationListTable/VerificationListTable';
import InfoCard from 'common/components/infoCard/InfoCard';
import GaugeIcon from 'assets/svg/gauge-white.svg';
import CalendarIcon from 'assets/svg/desktop_i_calendar.svg';
import colors from 'styles/export/colors.module.scss';
import { VerificationCounterListDto } from 'api/carVerification/models/VerificationDto';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';

const INFO_CARD_COLORS = [
    colors.counterTeal1,
    colors.counterTeal2,
];

type Props = {
    criteria: CarVerificationSearchCriteria;
    updateCriteria: (criteria: CarVerificationSearchCriteria) => void;
    genericPage?: boolean;
    isVehicle?: boolean;
    editable: boolean;
    tourReRender?: () => void;
    reRenderSearch: number;
    onVerificationsUpdated?: () => void | Promise<void>;
    vehicle?: VehicleDto;
};

const CarVerificationList: React.FC<Props> = ({ criteria: _criteria, isVehicle, editable, updateCriteria, tourReRender, reRenderSearch: _reRenderSearch, onVerificationsUpdated, vehicle }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();
    const { vehicleId, tabId } = useParams<{ vehicleId: string, itemType: string, itemId: string, tabId: string, type: string }>();

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasVerificationsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_VERIFICATIONS_WRITE']);
    const hasRequestVerificationsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_REQUEST_VERIFICATIONS_WRITE']);
    const hasCountersPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['CHARGES_TOTAL_READ']);

    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    const FILTERS_CACHE_KEY = 'vehicle-verifications-filters-' + (vehicleId ? 1 : 0);

    const [verifications, setVerifications] = useState<CarVerificationListItemDto[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [counters, setCounters] = useState<VerificationCounterListDto>();
    const [filters, setFilters] = useState<Filters>({});
    const [filtersTotal, setFiltersTotal] = useState(0);
    const isFirstLoad = useRef<boolean>(true);
    const previousCriteria = useRef<CarVerificationSearchCriteria>(_criteria);
    const countersLoaded = useRef<boolean>(false);
    const [reRenderSearch, setReRenderSearch] = useState<number>(0);

    const bidirectionalScroll = useBidirectionalScroll({
        cacheKey: FILTERS_CACHE_KEY,
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    const {
        loadedPageRange,
        isLoadingPrevious,
        scrollToRowId: scrollToItemId,
        shouldLoadAdjacentPages,
        setLoadedPageRange,
        setIsLoadingPrevious,
        resetScrollState,
        calculatePageFromIndex,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,
        finishRestoration,
        initiateRestoration,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
    } = bidirectionalScroll;

    const [criteria, setCriteria] = useState<CarVerificationSearchCriteria>({
        ..._criteria,
        vehicleId: vehicleId,
    });

    const getData = async (isLoadMore = false, shouldUpdateCounters = false) => {
        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            const crit = { ...criteria, vehicleId: vehicleId ?? criteria.vehicleId };

            saveOrdernationFilters();

            const res = await CarVerificationService.getList(crit);
            setTotalItems(res.totalItems);

            if (isLoadMore) {
                setVerifications(prev => [...prev, ...res.items]);
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setVerifications(res.items);
            }

            if (shouldUpdateCounters && hasCountersPolicy) {
                await getCounters(crit);
                countersLoaded.current = true;
            }

            ReactTooltip.rebuild();
            Loading.hide();
            setIsLoadingMore(false);

            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = criteria.page;
                const totalPages = Math.ceil(res.totalItems / (criteria.itemsPerPage || DEFAULT_PAGINATION_ITEMS_PER_PAGE));

                if (currentPage > 1) {
                    try {
                        const prevCriteria = { ...crit, page: currentPage - 1 };
                        const prevResult = await CarVerificationService.getList(prevCriteria);
                        setVerifications(prev => [...prevResult.items, ...prev]);
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.VERIFICATIONS, `Couldn't load previous page`, e);
                    }
                }

                if (currentPage < totalPages) {
                    try {
                        const nextCriteria = { ...crit, page: currentPage + 1 };
                        const nextResult = await CarVerificationService.getList(nextCriteria);
                        setVerifications(prev => [...prev, ...nextResult.items]);
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.VERIFICATIONS, `Couldn't load next page`, e);
                    }
                }

                finishRestoration();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VERIFICATIONS, `Couldn't get verifications list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
            setIsLoadingMore(false);
            resetScrollState();
        } finally {
            tourReRender && tourReRender();
        }
    };

    const getCounters = async (crit: CarVerificationSearchCriteria) => {
        try {
            const countersDB = await CarVerificationService.getCountersList(crit);
            setCounters(countersDB);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VERIFICATIONS, `Couldn't get verifications counters`, error);
        }
    };

    const fetchAllForExport = useCallback(async () => {
        const result = await CarVerificationService.getList({
            ...criteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items;
    }, [criteria]);

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setVerifications([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({ ...prev, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field }));
    };

    const loadMore = useCallback(() => {
        if (isLoadingMore || verifications.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, verifications.length, totalItems, loadedPageRange.max]);

    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;

        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;

        try {
            prepareLoadPrevious();
            const prevCriteria: CarVerificationSearchCriteria = {
                ...criteria,
                page: prevPage,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
            };
            const result = await CarVerificationService.getList(prevCriteria);
            setVerifications(prev => [...result.items, ...prev]);
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VERIFICATIONS, `Couldn't get previous verification page`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria, addToast, t]);

    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [verifications, isLoadingPrevious, adjustScrollAfterPrepend]);

    useEffect(() => {
        if (_criteria && !isFirstLoad.current) {
            setVerifications([]);
            resetScrollState();
            setTotalItems(0);
            countersLoaded.current = false;
            setCriteria(prev => ({ ...prev, ..._criteria, page: 1 }));
            setReRenderSearch(r => r + 1);
        }
    }, [_criteria]);

    useEffect(() => {
        if (isFirstLoad.current) {
            loadFilters();
        } else {
            const isLoadMore = criteria.page > loadedPageRange.max && verifications.length > 0;

            const prev = previousCriteria.current;
            const onlyOrderOrPageChanged =
                prev.startDate === criteria.startDate &&
                prev.vehicleId === criteria.vehicleId &&
                prev.driverResponsibleId === criteria.driverResponsibleId &&
                prev.type === criteria.type &&
                prev.interventionType === criteria.interventionType &&
                prev.annotationStatus === criteria.annotationStatus &&
                prev.allIn === criteria.allIn;

            const shouldLoadCounters = !countersLoaded.current || !onlyOrderOrPageChanged;
            getData(isLoadMore, shouldLoadCounters);
            previousCriteria.current = criteria;
        }
    }, [criteria]);

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);
        if (cachedFilters != null) {
            const f = JSON.parse(cachedFilters);

            if (f.scrollToItemId) {
                const startPage = initiateRestoration({
                    page: f.page || 1,
                    scrollToItemId: f.scrollToItemId,
                });

                const cleanedFilters = { ...f, scrollToItemId: undefined, page: 1 };
                localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(cleanedFilters));
                setFilters(cleanedFilters);
                updateTotalFilters(cleanedFilters);

                setCriteria(prev => ({
                    ...prev,
                    orderBy: f.orderBy,
                    orderColumn: f.orderColumn,
                    page: startPage,
                    itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                }));
                isFirstLoad.current = false;
                return;
            }

            f.scrollToItemId = undefined;
            f.page = 1;
            resetScrollState();
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(f));
            onChangeFilters(f, true);
        } else {
            getData(false, true);
        }

        isFirstLoad.current = false;
    };

    const debounced = useDebouncedCallback((value: string) => {
        if (value === criteria.allIn) return;
        setVerifications([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({ ...prev, page: 1, allIn: value }));
    }, 500);

    const updateTotalFilters = (_filters: Filters) => {
        let count = 0;
        if (_filters.driverResponsibleId) count++;
        if (_filters.endDate) count++;
        if (_filters.interventionType) count++;
        if (_filters.annotationStatus) count++;
        if (_filters.startDate) count++;
        if (_filters.type) count++;
        if (_filters.tripStartDate) count++;
        if (_filters.tripEndDate) count++;
        setFiltersTotal(count);
    };

    const onChangeFilters = (_filters: Filters, isOnLoad?: boolean) => {
        saveFiltersOnCache(_filters);
        updateTotalFilters(_filters);

        if (!isOnLoad) {
            setVerifications([]);
            resetScrollState();
            setTotalItems(0);
            countersLoaded.current = false;
        }

        setCriteria(prev => ({
            ...prev,
            ..._filters,
            page: (isOnLoad && _filters.page) ? _filters.page : 1,
        }));
        setReRenderSearch(r => r + 1);
    };

    const navigateToNew = () => {
        history.push(`/vehicles/details/${vehicleId}/${tabId}/new`);
    };

    const onRowClick = (item: CarVerificationListItemDto, event: any) => {
        event.preventDefault();
        event.stopPropagation();

        const itemIndex = verifications.findIndex(v => v.id === item.id);
        const actualPage = calculatePageFromIndex(itemIndex);

        const newFilters = { ...filters };
        newFilters.page = actualPage;
        (newFilters as any).scrollToItemId = item.id;
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        saveFiltersOnCache(newFilters);

        if (event.ctrlKey) {
            window.open(`/vehicles/details/${vehicleId}/${tabId}/details/${item.id}`, '_blank');
            return;
        }
        history.push(`/vehicles/details/${vehicleId}/${tabId}/details/${item.id}`);
    };

    const saveOrdernationFilters = () => {
        const newFilters = { ...filters };
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        saveFiltersOnCache(newFilters);
    };

    const saveFiltersOnCache = (f: Filters) => {
        setFilters(prev => ({ ...prev, ...f }));
        if ((f as any).scrollToItemId && f.page) {
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(f));
        } else {
            const { page, ...rest } = f;
            const { scrollToItemId, ...filtersWithoutScroll } = rest as any;
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(filtersWithoutScroll));
        }
    };

    const onDeleteFromList = () => {
        setVerifications([]);
        resetScrollState();
        countersLoaded.current = false;
        setCriteria(prev => ({ ...prev, page: 1 }));
        onVerificationsUpdated && onVerificationsUpdated();
    };

    const renderCounterBoxes = () => {
        return (
            <div className={styles.rowCounters}>
                {counters && (
                    <InfoCard
                        label={t('car_verification.list.number_verifications') as string}
                        value={counters.numberVerifications ?? 0}
                        icon={CalendarIcon}
                        backgroundColor={INFO_CARD_COLORS[0]}
                        className={styles.col2}
                    />
                )}
                {counters && (
                    <InfoCard
                        label={t('car_verification.list.total_kms') as string}
                        value={counters.totalKms ?? 0}
                        icon={GaugeIcon}
                        backgroundColor={INFO_CARD_COLORS[1]}
                        isKm
                        className={styles.col2}
                    />
                )}
            </div>
        );
    };

    return (
        <div className={styles.listWrapper}>
            <div className={styles.tabHeader}>
                <div>
                    <InputSearch inputContainerClassName={styles.inputContainer} onChangeValue={debounced} placeholder={t('car_verification.list.search_placeholder')} />
                </div>
                <div style={{ display: 'inline-flex' }}>
                    {hasRequestVerificationsWritePolicy && vehicle?.active &&
                        <Popover
                            contentContainerClassName={styles.filtersPopoverContainer}
                            containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                            positions={['bottom', 'left']}
                            align={'end'}
                            onClickOutside={() => updateTotalFilters(filters)}
                            content={setIsPopoverOpen => <RequestVerificationScreen
                                vehicleId={vehicleId}
                                onCancel={() => setIsPopoverOpen(false)}
                            />}
                        >
                            {(isPopoverOpen, setIsPopoverOpen) => (
                                <>
                                    <Button
                                        size={'extraSmall'}
                                        onClick={() => setIsPopoverOpen(!isPopoverOpen)}
                                        preset={'success'}
                                        text={t('car_verification.list.new_request_verification')}
                                        data-tip={t('car_verification.list.new_request_verification_tooltip')}
                                    />
                                    <ReactTooltip />
                                </>
                            )}
                        </Popover>}

                    <Popover
                        contentContainerClassName={styles.filtersPopoverContainer}
                        containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                        positions={['bottom', 'left']}
                        align={'end'}
                        onClickOutside={() => updateTotalFilters(filters)}
                        content={setIsPopoverOpen => <VerificationFiltersScreen
                            filters={filters}
                            onFilter={_filters => { setIsPopoverOpen(false); onChangeFilters(_filters) }}
                            onChange={_filters => updateTotalFilters(_filters)}
                        />}
                    >
                        {(isPopoverOpen, setIsPopoverOpen) => (
                            <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                {filtersTotal > 0 &&
                                    <div className={styles.counter}> <div className={styles.counterNumber}>{filtersTotal}</div> </div>
                                }
                            </ScreenHeaderButton>
                        )}
                    </Popover>
                    <ExportListingButton exportRef={exportListingRef} />
                    {hasVerificationsWritePolicy && editable && <ScreenHeaderButton icon={AddIcon} onClick={() => navigateToNew()} />}
                </div>
            </div>

            {counters && (
                <div className={styles.countersSection}>{renderCounterBoxes()}</div>
            )}

            {verifications.length > 0 || totalItems === 0 ? (
                <div className={styles.listTblWrapper}>
                    <VerificationListTable
                        reRenderSearch={reRenderSearch}
                        items={verifications}
                        totalItems={totalItems}
                        isLoadingMore={isLoadingMore}
                        hasMore={getHasMore(verifications.length, totalItems)}
                        vehicleId={vehicleId}
                        editable={editable}
                        onLoadMore={getOnLoadMore(loadMore)}
                        onTableFilter={onTableFilter}
                        onClickItem={onRowClick}
                        onDeleteFromList={onDeleteFromList}
                        criteria={criteria}
                        scrollToRowId={scrollToItemId}
                        hasMorePrevious={getHasMorePrevious()}
                        isLoadingPrevious={isLoadingPrevious}
                        onLoadPrevious={getOnLoadPrevious(loadPrevious)}
                        vehicle={vehicle}
                        onVerificationsUpdated={onVerificationsUpdated}
                        onFetchAllForExport={fetchAllForExport}
                        exportRef={exportListingRef}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default CarVerificationList;
